import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { FileAttachmentProps } from '../../../types/message';
import { MESSAGE_LIMITS, ACCEPTED_FILE_TYPES } from '../../../types/message';
import { ExtendedFileAttachment } from './types';
import { validateFile, simulateUpload } from './utils';
import { FileDropZone } from './FileDropZone';
import { FileList } from './FileList';

/**
 * ファイル添付コンポーネント
 * ドラッグ&ドロップ、ファイル選択、プレビュー機能を提供
 * Geminiのようなアップロード表示機能付き
 */
export const FileAttachment: React.FC<FileAttachmentProps> = ({
  files,
  onFilesChange,
  maxFiles = MESSAGE_LIMITS.MAX_FILES,
  maxFileSize = MESSAGE_LIMITS.MAX_FILE_SIZE,
  acceptedTypes = [ACCEPTED_FILE_TYPES.ALL],
  disabled = false,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [extendedFiles, setExtendedFiles] = useState<ExtendedFileAttachment[]>([]);

  // ファイル追加処理
  const addFiles = useCallback(async (newFiles: File[]) => {
    setError(null);

    if (files.length + newFiles.length > maxFiles) {
      setError(`添付できるファイルは最大${maxFiles}個までです`);
      return;
    }

    const validatedFiles: ExtendedFileAttachment[] = [];

    for (const file of newFiles) {
      const validationError = validateFile(file, maxFileSize, acceptedTypes);
      const fileAttachment: ExtendedFileAttachment = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        error: validationError || undefined,
        uploadState: validationError ? undefined : { isUploading: true, progress: 0, isCompleted: false },
      };

      // 画像の場合はプレビューを生成
      if (file.type.startsWith('image/') && !validationError) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileAttachment.preview = e.target?.result as string;
          setExtendedFiles(prev => [...prev]);
        };
        reader.readAsDataURL(file);
      }

      validatedFiles.push(fileAttachment);
    }

    setExtendedFiles(prev => [...prev, ...validatedFiles]);
    onFilesChange([...files, ...validatedFiles]);

    // エラーがないファイルのアップロードをシミュレート
    for (const file of validatedFiles) {
      if (!file.error) {
        await simulateUpload(file.id, setExtendedFiles);
      }
    }
  }, [files, maxFiles, maxFileSize, acceptedTypes, onFilesChange]);

  // ファイル削除
  const removeFile = useCallback((fileId: string) => {
    const updatedFiles = extendedFiles.filter(f => f.id !== fileId);
    setExtendedFiles(updatedFiles);
    onFilesChange(updatedFiles);
  }, [extendedFiles, onFilesChange]);

  return (
    <Box>
      <FileDropZone
        maxFiles={maxFiles}
        maxFileSize={maxFileSize}
        acceptedTypes={acceptedTypes}
        disabled={disabled}
        onFilesSelect={addFiles}
        error={error}
      />
      
      <FileList
        files={extendedFiles}
        maxFiles={maxFiles}
        onRemoveFile={removeFile}
      />
    </Box>
  );
}; 